import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AiOutlinePercentage } from 'react-icons/ai';
import Button from '../components/atoms/Button';
import LoadingLottie from '../components/atoms/LoadingLottie';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import { PaymentProvider } from '../models/payment-config';
import {
  useGetMPAuthLinkQuery, useGetPaymentConfigQuery, RootState, useDeletePaymentConfigMutation, useUpdatePaymentConfigMutation,
} from '../store';
import Select from '../components/atoms/Select/Select';
import AdminSectionTitle from '../components/AdminSectionTitle';
import Input from '../components/atoms/Input';
import { toastSuccess } from '../utils/toasts';
import { IconPlay2 } from '../components/icons';

const MercadoPagoIntegrationPage: React.FC = () => {
  const { business, businessLoading } = useGetSelectedBusiness();
  const [internal, setInternal] = useState<boolean>(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const isRootUser = user?.type === 'ROOT';
  const [comissionRate, setComissionRate] = useState<number>(0);
  const { data: paymentConfig, isLoading: paymentConfigLoading } = useGetPaymentConfigQuery(
    { businessId: business?.id ?? '', provider: PaymentProvider.MercadoPago },
    { skip: !business },
  );

  const [deletePaymentConfig, { isLoading: deletePaymentConfigLoading }] = useDeletePaymentConfigMutation();
  const [updatePaymentConfig, { isLoading: updatePaymentConfigLoading }] = useUpdatePaymentConfigMutation();

  const { data: authLink, isFetching: linkLoading, refetch: refetchAuthLink } = useGetMPAuthLinkQuery(
    { business_id: business?.id ?? '', internal },
    { skip: !business },
  );

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(businessLoading || paymentConfigLoading || linkLoading);
  }, [businessLoading, paymentConfigLoading, linkLoading]);

  useEffect(() => {
    // Convert decimal to percentage for display (e.g., 0.05 -> 5)
    setComissionRate(paymentConfig?.comission_rate ? (paymentConfig.comission_rate * 100) : 0);
  }, [paymentConfig]);

  useEffect(() => {
    if (business) {
      refetchAuthLink();
    }
  }, [internal, business, refetchAuthLink]);

  const handleIntegrate = () => {
    setIsLoading(true);
    window.open(authLink?.auth_link || '', '_self');
  };

  if (isLoading) {
    return <LoadingLottie />;
  }

  if (!business && !isLoading) {
    return <div>No se encontró la empresa</div>;
  }

  const handleUnlink = () => {
    deletePaymentConfig({ businessId: business?.id ?? '', paymentConfigId: paymentConfig?.id ?? '' });
  };

  const handleUpdateComissionRate = async () => {
    try {
      await updatePaymentConfig({
        businessId: business?.id ?? '',
        dto: { id: paymentConfig?.id ?? '', comission_rate: comissionRate / 100 },
      });
      toastSuccess('Comisión actualizada correctamente');
    } catch (error) {
      console.error('Error updating commission rate:', error);
    }
  };

  return (
    <div className="flex flex-col my-6 gap-6">
      <AdminSectionTitle
        title={`${business?.name}`}
        icon={<IconPlay2 className='w-12 h-12 rotate-180' />}
      />

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className='text-xl font-semibold'>Vincular cuenta de Mercado Pago</h1>
        <img src="/logo_mp_hor.png" className="w-60 glass-surface bg-white/20 p-1" alt="Mercado Pago logo" />
      </div>

      {paymentConfig && (
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col p-4 gap-2 glass-surface'>
            <h1 className='text-xl font-medium'>Cuenta vinculada correctamente</h1>
            <p className='text-lg'>Tu cuenta se encuentra vinculada de forma correcta. Cada venta que se realice ingresará el dinero de forma automática a tu cuenta. Debes tener en cuenta tener bien configurados los plazos de acreditamiento desde tu cuenta de mercado pago:</p>
            <span className='text-lg'>Tu negocio {'>'} Costos y cuotas {'>'} Checkout {'>'} Al instante</span>
          </div>
          <div className='flex flex-col p-4 gap-2 glass-surface'>
            <div className='flex flex-col md:flex-row gap-2'>
              <h1 className='text-xl font-medium md:w-full'>Configurar cargo por servicio</h1>
              <div className='md:ml-auto md:w-full relative'>
                <div className='absolute top-[15px] left-[10px] text-xl z-10 w-6 h-6 text-text-primary'><AiOutlinePercentage /></div>
                <Input
                  id="service-charge"
                  label=""
                  value={business?.online_service_charge ? business.online_service_charge * 100 : 15}
                  alignRight
                  disabled
                />
              </div>
            </div>
            <p className='text-lg'>Por defecto el cargo por servicio es del 15%, en caso de tener configurado acreditación instantanea y un 15% de cargo por servicio se te acreditará el monto total de cada entrada, en caso de modificar el porcentaje y no el plazo de venta se verá afectado el monto a acreditar en tu cuenta.</p>
          </div>
          <div className='flex flex-col p-4 gap-2 glass-surface'>
            <div className='flex flex-col md:flex-row gap-2'>
              <h1 className='text-xl font-medium md:w-full'>Configurar comisión de MercadoPago (Sin IVA)</h1>
              <div className='flex md:ml-auto md:w-full relative'>
                <div className='absolute top-[15px] left-[10px] text-xl z-10 w-6 h-6 text-text-primary'><AiOutlinePercentage /></div>
                <Input
                  id="comission-rate"
                  label=""
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    // Ensure the value is within reasonable bounds (0-100%)
                    if (value >= 0 && value <= 100) {
                      setComissionRate(value);
                    }
                  }}
                  value={comissionRate.toString()}
                  alignRight
                  disabled={!isRootUser || paymentConfigLoading}
                  type="number"
                  min="0"
                  max="100"
                  step="0.0001"
                />
                {isRootUser && (
                  <div className="w-fit mx-auto">
                    <Button variant="primary" onClick={handleUpdateComissionRate} loading={updatePaymentConfigLoading}>
                      <p className="px-12">Actualizar</p>
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <p className='text-lg'>Comisión de MercadoPago configurada para el plazo de acreditación. Comisión efectiva con IVA: {(paymentConfig.effective_comission_rate * 100).toFixed(2)}%</p>
          </div>
          <div className="w-fit mx-auto">
            <Button variant="primary" onClick={handleUnlink} loading={deletePaymentConfigLoading}>
              <p className="px-12">Desvincular cuenta</p>
            </Button>
          </div>
        </div>
      )}

      {!paymentConfig && (
        <div className='flex flex-col gap-6'>
          {isRootUser && (
            <div className="my-4 max-w-md px-4 pt-4 pb-2 glass-surface z-10">
              <h1 className='text-xl font-medium mb-2'>Tipo de split</h1>
              <Select
                id="internal-select"
                label=""
                options={[
                  { label: 'Cuenta de productora', value: 'false' },
                  { label: 'Cuenta de avantti', value: 'true' },
                ]}
                value={internal.toString()}
                onChange={(e) => {
                  setInternal(e.target.value === 'true');
                }}
              />
            </div>
          )}
          <div className='flex flex-col p-4 gap-2 glass-surface'>
            <h1 className='text-xl font-medium'>Vinculá tu cuenta</h1>
            <p className='text-lg'>Si querés vincular tu cuenta de Mercado Pago para que el ingreso de dinero sea inmediato a tu cuenta es necesario que tengas esa cuenta abierta en este mismo navegador y que lo hagas desde una computadora.</p>
            <p className='text-lg'>Verificá antes de tener la cuenta correcta abierta, en caso de ser necesario cerrá sesión con la cuenta actual e iniciá sesión con la cuenta correcta.</p>
          </div>
          <div className="w-fit mx-auto">
            <Button variant="primary" loading={linkLoading} onClick={handleIntegrate}>
              <p className="px-12">Vincular cuenta</p>
            </Button>
          </div>
        </div>
      )}

    </div>
  );
};

export default MercadoPagoIntegrationPage;
